import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { catchError, Observable, of } from 'rxjs';
import { IEmployee } from '../interfaces/IEmployee';
import { IEmployeeEmergencyContact } from '../interfaces/IEmployeeEmergencyContact';
import { IEmployeePassiveEmployment } from '../interfaces/IEmployeePassiveEmployment';
import { IEmployeeQualificationForm } from '../interfaces/employee/IEmployeeQualificationForm';
import { INotification } from '../interfaces/INotification';
import { NotificationService } from '../services/notification.service';
import { CreateEmergencyContact, DeleteEmergencyContact, UpdateEmergencyContact } from '../states/employee.actions';
import { emailOrPhoneRequired } from '../validators/mail-or-phone-required.validators';
import { validateDateChronology } from '../validators/date-chronology.validator';

export class PersonalInformationOperations {
  public static buildPersonalInformationForm(employee: IEmployee): FormGroup {
    return new FormGroup({
      visas: this.initVisas(employee),
      passport: this.initPassportForm(employee),
      qualifications: this.initQualificationForm(employee),
    });
  }

  public static initVisas(employee: IEmployee): FormArray {
    const visaFormGroupArray: FormGroup[] = [];

    if (employee) {
      for (let i = 0; i < employee.employeeVisas.length; i++) {
        visaFormGroupArray.push(
          new FormGroup({
            id: new FormControl(employee.employeeVisas[i].id ? employee.employeeVisas[i].id : null),
            countryId: new FormControl(employee.employeeVisas[i].countryId, [Validators.required]),
            expiresIn: new FormControl(employee.employeeVisas[i].expiresIn, [Validators.required]),
          }),
        );
      }
    }
    return new FormArray(visaFormGroupArray);
  }

  public static initEmergencyContacts(contacts: IEmployeeEmergencyContact[]): FormArray {
    const emergencyContactsFormGroupArray: FormGroup[] = [];

    if (contacts) {
      for (let i = 0; i < contacts.length; i++) {
        emergencyContactsFormGroupArray.push(
          new FormGroup(
            {
              id: new FormControl(contacts[i].id ? contacts[i].id : null),
              firstname: new FormControl(contacts[i].firstname, [Validators.required]),
              lastname: new FormControl(contacts[i].lastname, [Validators.required]),
              eMail: new FormControl(contacts[i].eMail, [Validators.email]),
              phoneNumber: new FormControl(contacts[i].phoneNumber),
              address: new FormGroup({
                street: new FormControl(contacts[i].address.street),
                houseNumber: new FormControl(contacts[i].address.houseNumber),
                zipCode: new FormControl(contacts[i].address.zipCode),
                city: new FormControl(contacts[i].address.city),
                country: new FormControl(contacts[i].address.country),
              }),
            },
            { validators: emailOrPhoneRequired },
          ),
        );
      }
    }
    return new FormArray(emergencyContactsFormGroupArray);
  }

  public static initPassportForm(employee: IEmployee): FormGroup {
    return new FormGroup({
      id: new FormControl(0),
      passportNumber: new FormControl(employee ? employee.passportNumber : ''),
      passportExpiryDate: new FormControl(employee ? employee.passportExpirationDate : null),
    });
  }

  public static initQualificationForm(employee?: IEmployee): IEmployeeQualificationForm {
    return new FormGroup<IEmployeeQualificationForm['controls']>({
      id: new FormControl<number | null>(0),
      isEuP: new FormControl<boolean | null>(employee ? employee.isEuP : false),
      isElectrician: new FormControl<boolean | null>(employee ? employee.isElectrician : false),
      isFirstResponder: new FormControl<boolean | null>(employee ? employee.isFirstResponder : false),
      hasHealthCheck: new FormControl<boolean | null>(employee ? employee.hasHealthCheck : false),
      approvalFleetVehicle: new FormControl<boolean | null>(employee ? employee.approvalFleetVehicle : false),
      approvalPrivateCar: new FormControl<boolean | null>(employee ? employee.approvalPrivateCar : false),
      hasDriverLicense: new FormControl<boolean | null>(employee ? employee.hasDriverLicense : false),
      hasTvInOrgChart: new FormControl<boolean | null>(employee ? employee.hasTvInOrgChart : false),
      hasCzechDrivingTraining: new FormControl<boolean | null>(employee ? (employee.hasCzechDrivingTraining ?? false) : false),
    });
  }

  public static updateEmployeeWithPassportForm(employee: IEmployee, passportForm: FormGroup): IEmployee {
    const updatedEmployee: IEmployee = {
      ...employee,
      passportNumber: passportForm.get('passportNumber')?.value as string,
      passportExpirationDate: ((): Date | null => {
        const rawValue: string | null = passportForm.get('passportExpiryDate')?.value as string | null;
        if (rawValue) {
          const dateValue: Date = new Date(rawValue);
          dateValue.setHours(23, 59, 59, 999);
          return dateValue;
        }
        return null;
      })(),
    };
    return updatedEmployee;
  }

  public static updateEmployeeWithQualificationForm(employee: IEmployee, qualificationForm: FormGroup): IEmployee {
    const updatedEmployee: IEmployee = {
      ...employee,
      isEuP: qualificationForm.get('isEuP')?.value as boolean,
      isElectrician: qualificationForm.get('isElectrician')?.value as boolean,
      isFirstResponder: qualificationForm.get('isFirstResponder')?.value as boolean,
      hasHealthCheck: qualificationForm.get('hasHealthCheck')?.value as boolean,
      approvalFleetVehicle: qualificationForm.get('approvalFleetVehicle')?.value as boolean,
      approvalPrivateCar: qualificationForm.get('approvalPrivateCar')?.value as boolean,
      hasDriverLicense: qualificationForm.get('hasDriverLicense')?.value as boolean,
      hasTvInOrgChart: qualificationForm.get('hasTvInOrgChart')?.value as boolean,
      hasCzechDrivingTraining: qualificationForm.get('hasCzechDrivingTraining')?.value as boolean,
    };
    return updatedEmployee;
  }

  public static getNewVisaItemForm(): FormGroup {
    return new FormGroup({
      countryId: new FormControl(null, [Validators.required]),
      expiresIn: new FormControl(null, [Validators.required]),
    });
  }

  public static getNewEmergencyContactItemForm(contact?: IEmployeeEmergencyContact): FormGroup {
    return new FormGroup(
      {
        id: new FormControl(contact ? contact.id : null),
        firstname: new FormControl(contact ? contact.firstname : '', [Validators.required]),
        lastname: new FormControl(contact ? contact.lastname : '', [Validators.required]),
        eMail: new FormControl(contact ? contact.eMail : '', [Validators.email]),
        phoneNumber: new FormControl(contact ? contact.phoneNumber : ''),
        address: new FormGroup({
          street: new FormControl(contact ? contact.address.street : ''),
          houseNumber: new FormControl(contact ? contact.address.houseNumber : ''),
          zipCode: new FormControl(contact ? contact.address.zipCode : ''),
          city: new FormControl(contact ? contact.address.city : ''),
          country: new FormControl(contact ? contact.address.country : ''),
        }),
      },
      { validators: emailOrPhoneRequired },
    );
  }

  public static showNewEmergencyContactErrorMessage(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'employee.personalInformation.emergencyContacts.notification.createAccount.error.title',
      desc: 'employee.personalInformation.emergencyContacts.notification.createAccount.error.desc',
    };
    notificationService.showMessage(notification);
    return of(null);
  }
  public static showSaveEmergencyContactErrorMessage(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'employee.personalInformation.emergencyContacts.notification.saveAccount.error.title',
      desc: 'employee.personalInformation.emergencyContacts.notification.saveAccount.error.desc',
    };
    notificationService.showMessage(notification);
    return of(null);
  }
  public static showDeleteEmergencyContactErrorMessage(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'employee.personalInformation.emergencyContacts.notification.deleteAccount.error.title',
      desc: 'employee.personalInformation.emergencyContacts.notification.deleteAccount.error.desc',
    };
    notificationService.showMessage(notification);
    return of(null);
  }

  public static addContact(
    employeeId: number,
    contact: IEmployeeEmergencyContact,
    store: Store,
    notificationService: NotificationService,
  ): Observable<void> {
    return store
      .dispatch(new CreateEmergencyContact({ id: employeeId.toString(), contact }))
      .pipe(catchError(() => PersonalInformationOperations.showNewEmergencyContactErrorMessage(notificationService)));
  }

  public static updateContact(
    employeeId: number,
    contact: IEmployeeEmergencyContact,
    store: Store,
    notificationService: NotificationService,
  ): Observable<void> {
    return store
      .dispatch(new UpdateEmergencyContact({ id: employeeId.toString(), contact }))
      .pipe(catchError(() => PersonalInformationOperations.showSaveEmergencyContactErrorMessage(notificationService)));
  }

  public static deleteContact(
    employeeId: number,
    contactId: number,
    store: Store,
    notificationService: NotificationService,
  ): Observable<void> {
    return store
      .dispatch(new DeleteEmergencyContact({ id: employeeId.toString(), emergencyContactId: contactId.toString() }))
      .pipe(catchError(() => PersonalInformationOperations.showDeleteEmergencyContactErrorMessage(notificationService)));
  }

  public static initPassiveEmployments(passiveEmployments: IEmployeePassiveEmployment[]): FormArray {
    const passiveEmploymentFormGroupArray: FormGroup[] = [];

    if (passiveEmployments) {
      for (let i = 0; i < passiveEmployments.length; i++) {
        passiveEmploymentFormGroupArray.push(
          new FormGroup({
            id: new FormControl(passiveEmployments[i].id ? passiveEmployments[i].id : null),
            begin: new FormControl(passiveEmployments[i].begin, [Validators.required]),
            end: new FormControl(passiveEmployments[i].end, [Validators.required]),
            tmgEmployeeId: new FormControl(passiveEmployments[i].tmgEmployeeId),
          }),
        );
      }
    }
    return new FormArray(passiveEmploymentFormGroupArray);
  }

  public static getNewPassiveEmploymentForm(passiveEmployment?: IEmployeePassiveEmployment, employeeId?: number): FormGroup {
    return new FormGroup(
      {
        id: new FormControl(passiveEmployment ? passiveEmployment.id : 0),
        begin: new FormControl(passiveEmployment ? passiveEmployment.begin : new Date(), [Validators.required]),
        end: new FormControl(passiveEmployment ? passiveEmployment.end : new Date(), [Validators.required]),
        tmgEmployeeId: new FormControl(passiveEmployment ? passiveEmployment.tmgEmployeeId : employeeId),
      },
      {
        validators: validateDateChronology('begin', 'end'),
      },
    );
  }
}
